import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Card from "@/v2-ui/card"
import { DotText } from "@/v2-backoffice-client/components/dot"
import type { SharingRiskDetailFragment } from "@/v2-backoffice-client/signIn/__types__/signIn.detail.fragment"
import { getSharingRiskVariant } from "@/v2-backoffice-client/signIn/sharingRisk/sharingRisk.constants"
import * as utils from "@/v2-backoffice-client/signIn/sharingRisk/sharingRisk.utils"
import SharingRiskSessions from "@/v2-backoffice-client/signIn/sharingRisk/SharingRiskSessions"

export type SharingRiskCardProps = ComponentProps<"div"> & {
  risk: SharingRiskDetailFragment | null | undefined
  /**
   * Called when a signal value is clicked, so the surrounding page can filter
   * its sign-in list to the events behind that value. The signal key says
   * which field the value came from (see utils.activityMatchesFocused).
   * Values are only clickable when the card is given this (see
   * utils.isDrillable for which signals qualify).
   */
  onFocusValue?: (value: string, signalKey: string) => void
  /** The value currently filtered on, highlighted in the table. */
  focusedValue?: string | null
}

// The rows come from the backend's own breakdown, which is the sum that
// produced Score. The card used to reapply the point tables here, and drifted:
// it knew six signals while the scorer had nine, so the footer read 1 beside a
// header reading 9. Nothing in this file decides points any more.
function SharingRiskCard(props: SharingRiskCardProps) {
  const { risk, className, onFocusValue, focusedValue, ...divProps } = props
  const level = risk?.Level ?? 1
  const variant = getSharingRiskVariant(level)
  const name = risk?.LevelName ?? "None"

  const signals = risk?.Signals ?? []
  const totalPts = signals.reduce((sum, signal) => sum + signal.Points, 0)
  const windowDays = risk?.WindowDays ?? 0
  const scale = risk?.ThresholdScale ?? 1
  const drivers = utils.summarizeDrivers(signals)

  return (
    <Card
      className={cn("sharing-risk-card", className)}
      data-feature-code="UI_BackofficeSignIns_SharingRiskCard_Root"
      {...divProps}
    >
      <Card.Header>
        <Card.Title className="flex items-center gap-2 font-medium">
          Sharing Risk:{" "}
          <DotText variant={variant}>
            {name} - Score: {risk?.Score ?? 0}
          </DotText>
        </Card.Title>

        {drivers && (
          <Card.Description className="text-popover-foreground">
            {drivers}
          </Card.Description>
        )}

        {risk?.LevelDescription && (
          <Card.Description>{risk.LevelDescription}</Card.Description>
        )}

        {risk?.ScoredSource && (
          <Card.Description>
            Scored on the worst single app: {risk.ScoredSource}.
            {(risk?.TotalDistinctIpCount ?? 0) > (risk?.DistinctIpCount ?? 0) &&
              ` Across all apps: ${risk.TotalDistinctIpCount} IPs, ${risk.TotalDistinctLocationCount} locations, ${risk.TotalDistinctDeviceCount} devices (not scored).`}
          </Card.Description>
        )}

        {windowDays > 0 && (
          <Card.Description>
            {utils.formatWindow(windowDays, scale)}
          </Card.Description>
        )}
      </Card.Header>

      <Card.Content className="flex flex-col gap-3">
        <table className="sharing-risk-table">
          <thead>
            <tr>
              <th>Signal</th>
              <th>Count</th>
              <th>Details</th>
              <th className="text-right">Points</th>
            </tr>
          </thead>
          <tbody>
            {signals.map((signal) => {
              const drillable =
                Boolean(onFocusValue) && utils.isDrillable(signal.Key)
              return (
                <tr key={signal.Key}>
                  <td>{signal.Label}</td>
                  <td>{signal.Count}</td>
                  <td className="sharing-risk-table-details">
                    {signal.Key === "concurrentSessions" ? (
                      <SharingRiskSessions
                        sessions={risk?.ConcurrentSessionPairs}
                      />
                    ) : signal.Values.length > 0 ? (
                      <ul>
                        {/* Values are a sorted distinct set, so the value is the key. */}
                        {signal.Values.map((value) => (
                          <li key={value}>
                            {"- "}
                            {drillable ? (
                              <button
                                type="button"
                                className={cn(
                                  "sharing-risk-value-button",
                                  focusedValue === value &&
                                    "sharing-risk-value-button--active",
                                )}
                                onClick={() =>
                                  onFocusValue?.(value, signal.Key)
                                }
                                data-feature-code="UI_BackofficeSignIns_SharingRiskCard_FilterByValue"
                                title="Show only the sign-ins behind this value"
                              >
                                {value}
                              </button>
                            ) : (
                              value
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : signal.Points > 0 ? (
                      // A note earns the space only when it explains points
                      // that were actually awarded. For a row that scored
                      // nothing, the thresholds are noise; they stay in the
                      // explainer below.
                      signal.Note
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="text-right">
                    {utils.formatPts(signal.Points)}
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>Total score</td>
              <td className="text-right">{totalPts}</td>
            </tr>
          </tfoot>
        </table>

        <details className="sharing-risk-explainer">
          <summary data-feature-code="UI_BackofficeSignIns_SharingRiskCard_Explainer">
            How is this calculated?
          </summary>
          <div className="sharing-risk-explainer-body">
            <p className="text-xs text-muted-foreground mb-2">
              Risk is a weighted score across the signals below, then mapped to
              a level. Every signal that contributed is listed in the table
              above, and the table always adds up to the score.
            </p>

            <div className="sharing-risk-explainer-grid">
              {signals.map((signal) => (
                <div className="sharing-risk-explainer-signal" key={signal.Key}>
                  <p className="sharing-risk-explainer-signal-name">
                    {signal.Label}
                  </p>
                  <p className="sharing-risk-explainer-signal-rule">
                    {signal.Note ?? "—"}
                  </p>
                </div>
              ))}
            </div>

            <p className="text-xs text-muted-foreground mt-2">
              Counting rules: IPv6 addresses count per /48 because mobile
              networks hand out a fresh address per session, browser versions
              are ignored because an update is not a new device, and a country
              change only counts as impossible travel when the gap is shorter
              than the distance allows. Signals that accumulate over the period
              have their thresholds widened for longer periods, so the same
              behavior scores the same whether you look at a week or a year.
            </p>

            <div className="sharing-risk-explainer-levels">
              <DotText variant="secondary">0–1 None</DotText>
              <DotText variant="info">2–4 Low</DotText>
              <DotText variant="warning">5–7 Moderate</DotText>
              <DotText variant="orange">8–12 High</DotText>
              <DotText variant="destructive">13+ Critical</DotText>
            </div>
          </div>
        </details>
      </Card.Content>
    </Card>
  )
}

export default SharingRiskCard
